import { SelectOption } from '@ardoq/select';
import {
  APIFieldType,
  APISurveyAttributes,
  ArdoqId,
  PersistedFieldSubQuestion,
  SurveyQuestionType,
} from '@ardoq/api-types';
import { useState } from 'react';
import {
  Fields,
  FieldAnswerValue,
  ConditionAnswer,
  GetAISuggestions,
  PartialAnswer,
  ReferenceAnswer,
  FetchComponentOptionsWithQuery,
  GenerateComponentDescription,
} from '../types';
import {
  getFieldTypeFromAttributeName,
  getFieldType,
} from '../utils/fieldOperations';
import {
  SetNestedAnswerPayload,
  AddNestedDependentAnswerPayload,
  RemoveNestedDependentAnswerPayload,
  UpdateReferenceComponentPayload,
} from './ReferenceQuestions/referenceTypes';
import { isAnsweredParentQuestion } from '../utils/answerPredicates';
import { isReferenceQuestion } from './questionPredicates';
import {
  getNoAccessReferenceCount,
  getRelevantConditionalQuestions,
  noAccessReferencesMeetMaxRangeValidation,
  setupAttributeQuestionData,
  setupDateRangeFieldQuestionData,
  setupFieldQuestionData,
} from './questionOperations';
import {
  getConditionalFieldQuestionAnswers,
  getReferenceQuestionAnswer,
} from '../utils/answerOperations';
import { SurveyAttributeQuestion } from './SurveyAttributeQuestion';
import { SurveyDateRangeFieldQuestion } from './SurveyDateRangeFieldQuestion';
import { SurveyFieldQuestion } from './FieldQuestion/SurveyFieldQuestion';
import { SurveyReferenceAndDependentQuestionField } from './surveyQuestionAtoms';
import { SurveyReferenceAndDependentQuestionHeader } from './SurveyReferenceAndDependentQuestionHeader';
import { isDateRangeFieldType } from '@ardoq/date-range';
import { Stack } from '@ardoq/layout';
import { Locale } from '@ardoq/locale';
import { exhaustiveCheck } from '@ardoq/common-helpers';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale } from '@ardoq/i18n';
import { SurveyDependentReferenceQuestion } from './ReferenceQuestions/SurveyDependentReferenceQuestion';
import { QuestionContentWrapper } from './questionsTypes';
import { getConditionAnswerTitle } from '../utils/stringUtils';

export type SurveyDependentQuestionsProps = {
  questions?: PersistedFieldSubQuestion[];
  survey: APISurveyAttributes;
  componentId: ArdoqId | undefined;
  fields: Fields;
  parentAnswer?: FieldAnswerValue;
  conditionalAnswer?: ConditionAnswer;
  parentQuestionTitle: string;
  parentFieldType?: APIFieldType;
  parentQuestionId: ArdoqId;
  parentQuestionIsReadonly: boolean;
  parentQuestionIsReferenceQuestion: boolean;
  isLocked: boolean;
  isSaving: boolean;
  componentName: string | null;
  componentTypeName: string;
  currentLocale: Locale;
  i18nLocale?: I18nLocale;
  hasAttemptedSubmission: boolean;
  hasAutocompleteFeature: boolean;
  hasReferenceSuggestionsFeature: boolean;
  hasEditableReferencesInQuestionsFeature: boolean;
  isEmbedded: boolean;
  hideReadOnlyLabel: boolean;
  QuestionContentWrapper: QuestionContentWrapper;
  highlightUnansweredPriorityQuestions: boolean;
  getDuplicate: (value: string) => null | { path: string };
  setAnswer: (answer: PartialAnswer) => void;
  setNestedAnswer: (answer: SetNestedAnswerPayload) => void;
  getExistingComponentByName: (name: string) => void;
  addNestedDependentAnswer: (
    nestedAnswer: AddNestedDependentAnswerPayload
  ) => void;
  removeNestedDependentAnswer: (
    nestedAnswer: RemoveNestedDependentAnswerPayload
  ) => void;
  updateReferenceComponent: (
    updateReferenceComponentPayload: UpdateReferenceComponentPayload
  ) => void;
  generateComponentDescription: GenerateComponentDescription;
  trackDescriptionGenerated: (value: unknown) => () => void;
  trackDescriptionGeneratedFailed: () => void;
  trackCreatedReferenceFromQuestion: (
    selectedAISuggestedOption: boolean
  ) => void;
  getAISuggestions: GetAISuggestions;
  fetchComponentOptionsWithQuery: FetchComponentOptionsWithQuery;
  userOptions: SelectOption<ArdoqId>[];
};

export const SurveyDependentQuestions = ({
  questions,
  survey,
  componentId,
  fields = {},
  parentAnswer,
  conditionalAnswer,
  parentQuestionTitle,
  parentFieldType,
  parentQuestionId,
  parentQuestionIsReadonly,
  parentQuestionIsReferenceQuestion,
  isLocked,
  isSaving,
  componentName,
  componentTypeName,
  currentLocale,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  hasAttemptedSubmission,
  hasAutocompleteFeature,
  hasReferenceSuggestionsFeature,
  hasEditableReferencesInQuestionsFeature,
  isEmbedded,
  hideReadOnlyLabel,
  QuestionContentWrapper,
  highlightUnansweredPriorityQuestions,
  getExistingComponentByName,
  getDuplicate,
  setAnswer,
  setNestedAnswer,
  addNestedDependentAnswer,
  removeNestedDependentAnswer,
  updateReferenceComponent,
  generateComponentDescription,
  trackDescriptionGenerated,
  trackDescriptionGeneratedFailed,
  trackCreatedReferenceFromQuestion,
  getAISuggestions,
  fetchComponentOptionsWithQuery,
  userOptions,
}: SurveyDependentQuestionsProps) => {
  const answers = getConditionalFieldQuestionAnswers(conditionalAnswer);

  const [isCollapsed, setIsCollapsed] = useState(false);

  const relevantDependentQuestions = getRelevantConditionalQuestions(
    questions,
    parentAnswer,
    parentFieldType
  );

  if (!relevantDependentQuestions) return null;

  const dependentQuestionsTitle = getConditionAnswerTitle(
    parentAnswer,
    parentQuestionTitle,
    i18nLocale
  );

  const allReleventDependentQuestionsNonAccessible =
    relevantDependentQuestions?.every(question => {
      if (!isReferenceQuestion(question)) return false;
      const answer = getReferenceQuestionAnswer(question._id, answers);
      const noAccessReferenceCount = getNoAccessReferenceCount(answer);
      return noAccessReferencesMeetMaxRangeValidation(
        noAccessReferenceCount,
        question.validators
      );
    });

  if (allReleventDependentQuestionsNonAccessible) return null;
  return relevantDependentQuestions.length ? (
    <div>
      <SurveyReferenceAndDependentQuestionHeader
        isCollapsed={isCollapsed}
        leftContent={dependentQuestionsTitle}
        // no required questions within dependent questions
        requiredAnswersMissing={false}
        i18nLocale={i18nLocale}
        setIsCollapsed={setIsCollapsed}
      />
      <SurveyReferenceAndDependentQuestionField
        // only relevant for reference questions
        $isDisabled={false}
        $isCollapsed={isCollapsed}
      >
        <Stack gap="xlarge">
          {relevantDependentQuestions.map(question => {
            const readonly =
              question.readonly ||
              (parentQuestionIsReadonly && parentQuestionIsReferenceQuestion);
            switch (question.type) {
              case SurveyQuestionType.REFERENCE: {
                const answer = getReferenceQuestionAnswer(
                  question._id,
                  answers
                );
                const value = answer?.value ?? [];
                return (
                  <SurveyDependentReferenceQuestion
                    userOptions={userOptions}
                    question={question}
                    answer={answer}
                    readonly={readonly}
                    hasSubQuestions={Boolean(
                      question.properties.questions?.length
                    )}
                    componentId={componentId}
                    key={question._id}
                    fields={fields}
                    parentQuestionId={parentQuestionId}
                    value={value}
                    survey={survey}
                    currentLocale={currentLocale}
                    i18nLocale={i18nLocale}
                    hasAttemptedSubmission={hasAttemptedSubmission}
                    hasAutocompleteFeature={hasAutocompleteFeature}
                    hasReferenceSuggestionsFeature={
                      hasReferenceSuggestionsFeature
                    }
                    hasEditableReferencesInQuestionsFeature={
                      hasEditableReferencesInQuestionsFeature
                    }
                    isEmbedded={isEmbedded}
                    noAccessReferences={getNoAccessReferenceCount(
                      answer as ReferenceAnswer | undefined
                    )}
                    hideReadOnlyLabel={hideReadOnlyLabel}
                    QuestionContentWrapper={QuestionContentWrapper}
                    highlightUnansweredPriorityQuestions={
                      highlightUnansweredPriorityQuestions
                    }
                    setAnswer={setAnswer}
                    getDuplicate={getDuplicate}
                    getExistingComponentByName={getExistingComponentByName}
                    setNestedAnswer={setNestedAnswer}
                    addNestedDependentAnswer={addNestedDependentAnswer}
                    removeNestedDependentAnswer={removeNestedDependentAnswer}
                    updateReferenceComponent={updateReferenceComponent}
                    generateComponentDescription={generateComponentDescription}
                    trackDescriptionGenerated={trackDescriptionGenerated}
                    trackDescriptionGeneratedFailed={
                      trackDescriptionGeneratedFailed
                    }
                    trackCreatedReferenceFromQuestion={
                      trackCreatedReferenceFromQuestion
                    }
                    getAISuggestions={getAISuggestions}
                    fetchComponentOptionsWithQuery={
                      fetchComponentOptionsWithQuery
                    }
                  />
                );
              }
              case SurveyQuestionType.ATTRIBUTE: {
                const { originalValue, validateDuplicateName, answer, value } =
                  setupAttributeQuestionData({
                    question,
                    answers,
                    isSaving,
                    defaultParentId: survey.newComponentParentId,
                  });
                return (
                  <SurveyAttributeQuestion
                    userOptions={userOptions}
                    surveyId={survey._id}
                    question={question}
                    answer={answer}
                    componentId={componentId}
                    readonly={readonly}
                    originalValue={originalValue}
                    validateDuplicateName={validateDuplicateName}
                    isLocked={isLocked}
                    componentName={componentName}
                    componentTypeName={componentTypeName}
                    key={question._id}
                    value={value}
                    hasAttemptedSubmission={hasAttemptedSubmission}
                    currentLocale={currentLocale}
                    i18nLocale={i18nLocale}
                    hasAutocompleteFeature={hasAutocompleteFeature}
                    type={getFieldTypeFromAttributeName(
                      question.properties.attributeName
                    )}
                    parentName={
                      isAnsweredParentQuestion(answer)
                        ? answer.parentName
                        : undefined
                    }
                    isEmbedded={isEmbedded}
                    hideReadOnlyLabel={hideReadOnlyLabel}
                    QuestionContentWrapper={QuestionContentWrapper}
                    highlightUnansweredPriorityQuestions={
                      highlightUnansweredPriorityQuestions
                    }
                    getExistingComponentByName={getExistingComponentByName}
                    setAnswer={setAnswer}
                    getDuplicate={getDuplicate}
                    generateComponentDescription={generateComponentDescription}
                    onDescriptionReceived={trackDescriptionGenerated(value)}
                    onLLMCouldNotGenerateDescription={
                      trackDescriptionGeneratedFailed
                    }
                    getAISuggestions={getAISuggestions}
                    fetchComponentOptionsWithQuery={
                      fetchComponentOptionsWithQuery
                    }
                  />
                );
              }
              case SurveyQuestionType.FIELD: {
                const type = getFieldType(
                  question.properties.fieldName,
                  fields
                );
                if (isDateRangeFieldType(type)) {
                  const { value, startDate, endDate, answer } =
                    setupDateRangeFieldQuestionData({
                      fields,
                      question,
                      answers,
                    });
                  return (
                    <SurveyDateRangeFieldQuestion
                      question={question}
                      answer={answer}
                      readonly={readonly}
                      isLocked={isLocked}
                      startDateField={startDate}
                      endDateField={endDate}
                      key={question._id}
                      value={value}
                      currentLocale={currentLocale}
                      i18nLocale={i18nLocale}
                      hideReadOnlyLabel={hideReadOnlyLabel}
                      QuestionContentWrapper={QuestionContentWrapper}
                      highlightUnansweredPriorityQuestions={
                        highlightUnansweredPriorityQuestions
                      }
                      setAnswer={setAnswer}
                    />
                  );
                }
                const {
                  field,
                  originalValue,
                  validateDuplicateName,
                  value,
                  answer,
                } = setupFieldQuestionData({
                  fields,
                  question,
                  answers,
                  isSaving,
                });
                return (
                  <SurveyFieldQuestion
                    userOptions={userOptions}
                    surveyId={survey._id}
                    question={question}
                    answer={answer}
                    componentId={componentId}
                    readonly={readonly}
                    originalValue={originalValue}
                    type={type}
                    field={field}
                    validateDuplicateName={validateDuplicateName}
                    isLocked={isLocked}
                    componentName={componentName}
                    componentTypeName={componentTypeName}
                    key={question._id}
                    value={value}
                    hasAttemptedSubmission={hasAttemptedSubmission}
                    currentLocale={currentLocale}
                    i18nLocale={i18nLocale}
                    hasAutocompleteFeature={hasAutocompleteFeature}
                    isEmbedded={isEmbedded}
                    hideReadOnlyLabel={hideReadOnlyLabel}
                    QuestionContentWrapper={QuestionContentWrapper}
                    highlightUnansweredPriorityQuestions={
                      highlightUnansweredPriorityQuestions
                    }
                    getExistingComponentByName={getExistingComponentByName}
                    setAnswer={setAnswer}
                    getDuplicate={getDuplicate}
                    getAISuggestions={getAISuggestions}
                    fetchComponentOptionsWithQuery={
                      fetchComponentOptionsWithQuery
                    }
                    generateComponentDescription={generateComponentDescription}
                  />
                );
              }
              default:
                return exhaustiveCheck(question);
            }
          })}
        </Stack>
      </SurveyReferenceAndDependentQuestionField>
    </div>
  ) : null;
};
